import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import Navbar from './Navbar';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiMonitor, FiPlus, FiActivity, FiWifi, FiWifiOff, FiSettings, FiEdit, FiTrash2, FiMapPin, FiClock } = FiIcons;

const MonitorsPage = () => {
  const [monitors, setMonitors] = useState([
    {
      id: 1,
      name: 'Lobby Display',
      location: 'Main Lobby',
      status: 'online',
      lastSeen: '2 minutes ago',
      resolution: '1920x1080',
      currentPlaylist: 'Welcome Messages'
    },
    {
      id: 2,
      name: 'Cafe Screen',
      location: 'Cafe Area',
      status: 'offline',
      lastSeen: '2 hours ago',
      resolution: '1920x1080',
      currentPlaylist: 'Menu & Promotions'
    },
    {
      id: 3,
      name: 'Conference Room A',
      location: 'Meeting Room A',
      status: 'online',
      lastSeen: 'Just now',
      resolution: '3840x2160',
      currentPlaylist: 'Corporate Updates'
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newMonitor, setNewMonitor] = useState({
    name: '',
    location: ''
  });

  const handleAddMonitor = () => {
    if (!newMonitor.name || !newMonitor.location) {
      toast.error('Please fill in all fields');
      return;
    }

    const monitor = {
      id: Date.now(),
      name: newMonitor.name,
      location: newMonitor.location,
      status: 'pending',
      lastSeen: 'Never',
      resolution: '1920x1080',
      currentPlaylist: 'None'
    };

    setMonitors([...monitors, monitor]);
    setNewMonitor({ name: '', location: '' });
    setShowAddModal(false);
    toast.success('Monitor added successfully!');
  };

  const generatePairingCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-green-400';
      case 'offline':
        return 'text-red-400';
      case 'pending':
        return 'text-yellow-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return FiWifi;
      case 'offline':
        return FiWifiOff;
      case 'pending':
        return FiClock;
      default:
        return FiActivity;
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex justify-between items-center mb-8"
          >
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Monitors</h1>
              <p className="text-white/70">
                Manage your digital signage displays
              </p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
            >
              <SafeIcon icon={FiPlus} />
              <span>Add Monitor</span>
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Monitors</p>
                  <p className="text-2xl font-bold text-white">{monitors.length}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiMonitor} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Online</p>
                  <p className="text-2xl font-bold text-white">
                    {monitors.filter(m => m.status === 'online').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiWifi} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Offline</p>
                  <p className="text-2xl font-bold text-white">
                    {monitors.filter(m => m.status === 'offline').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-pink-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiWifiOff} className="text-white text-xl" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Monitors List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/20">
              <h2 className="text-xl font-semibold text-white">All Monitors</h2>
            </div>
            
            <div className="divide-y divide-white/10">
              {monitors.map((monitor) => (
                <div key={monitor.id} className="p-6 hover:bg-white/5 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${
                        monitor.status === 'online' ? 'from-green-400 to-emerald-400' :
                        monitor.status === 'offline' ? 'from-red-400 to-pink-400' :
                        'from-yellow-400 to-orange-400'
                      } rounded-xl flex items-center justify-center`}>
                        <SafeIcon icon={FiMonitor} className="text-white text-xl" />
                      </div>
                      
                      <div>
                        <h3 className="text-white font-semibold text-lg">{monitor.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-white/70">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiMapPin} className="text-xs" />
                            <span>{monitor.location}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon 
                              icon={getStatusIcon(monitor.status)} 
                              className={`text-xs ${getStatusColor(monitor.status)}`} 
                            />
                            <span className={getStatusColor(monitor.status)}>
                              {monitor.status.charAt(0).toUpperCase() + monitor.status.slice(1)}
                            </span>
                          </div>
                          <span>Last seen: {monitor.lastSeen}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <div className="text-right text-sm">
                        <p className="text-white/70">Resolution</p>
                        <p className="text-white">{monitor.resolution}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-white/70">Current Playlist</p>
                        <p className="text-white">{monitor.currentPlaylist}</p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                          <SafeIcon icon={FiSettings} className="text-white/70 hover:text-white" />
                        </button>
                        <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                          <SafeIcon icon={FiEdit} className="text-white/70 hover:text-white" />
                        </button>
                        <button className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors">
                          <SafeIcon icon={FiTrash2} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Add Monitor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-md w-full"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Add New Monitor</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Monitor Name
                </label>
                <input
                  type="text"
                  value={newMonitor.name}
                  onChange={(e) => setNewMonitor({ ...newMonitor, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                  placeholder="e.g., Lobby Display"
                />
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={newMonitor.location}
                  onChange={(e) => setNewMonitor({ ...newMonitor, location: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                  placeholder="e.g., Main Lobby"
                />
              </div>

              <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4">
                <p className="text-blue-300 text-sm mb-2">Pairing Code:</p>
                <p className="text-white font-mono text-lg">{generatePairingCode()}</p>
                <p className="text-blue-300/80 text-xs mt-2">
                  Enter this code on your Android TV to connect the monitor
                </p>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMonitor}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                Add Monitor
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MonitorsPage;